import { useRef, useCallback } from "react";
import { isBillingLlmBlockedError } from "~/features/billing/types";
import { createBillingLlmBlockedError } from "~/features/billing/utils/parse-billing-blocked-response";
import { parseBillingBlockedResponseBody } from "~/features/billing/utils/parse-billing-blocked-response";
import { meditationVerboseDebugLog } from "~/features/meditations/utils/browser-debug";
import type { SSEEvent } from "./types";

function isReconnectableSSEError(error: unknown): boolean {
    if (isBillingLlmBlockedError(error)) {
        return false;
    }

    return !(
        error instanceof Error &&
        error.message === "Unauthorized - please log in again"
    );
}

/**
 * Configuration for useSSEConnection hook
 */
export interface UseSSEConnectionConfig {
    url: string;
    onEvent: (event: SSEEvent) => void;
    onError: (error: Error) => void;
    reconnect?: {
        enabled: boolean;
        maxRetries: number;
        onReconnecting?: () => void;
        onReconnectFailed?: () => void;
        shouldReconnect?: () => boolean;
    };
}

/**
 * Hook for managing SSE (Server-Sent Events) connection lifecycle.
 *
 * AIDEV-NOTE: This hook uses fetch + ReadableStream instead of native EventSource.
 * EventSource with withCredentials has issues in Vite dev server - requests silently fail.
 * Fetch with credentials: 'include' handles auth cookies reliably.
 *
 * This hook handles:
 * - Fetch-based SSE connection with credentials
 * - Stream parsing and event dispatch
 * - Error handling with graceful close detection
 * - Cleanup on unmount via AbortController
 */
export function useSSEConnection(config: UseSSEConnectionConfig) {
    const configRef = useRef(config);
    const activeConnectionIdRef = useRef(0);
    const activeConnectionUrlRef = useRef<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const retryCountRef = useRef(0);
    const retryTimeoutRef = useRef<number | null>(null);
    const connectRef = useRef<() => void>(() => {});

    configRef.current = config;

    const connect = useCallback(() => {
        const currentConfig = configRef.current;

        // Abort existing connection
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        if (retryTimeoutRef.current !== null) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }

        // Create new abort controller for this connection
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const connectionId = activeConnectionIdRef.current + 1;
        activeConnectionIdRef.current = connectionId;
        activeConnectionUrlRef.current = currentConfig.url;
        let receivedMainUrl = false;
        // AIDEV-NOTE: Drop stale stream events after props move to a different SSE URL.
        const isActiveConnection = () =>
            activeConnectionIdRef.current === connectionId &&
            activeConnectionUrlRef.current === currentConfig.url &&
            configRef.current.url === currentConfig.url &&
            abortControllerRef.current === abortController &&
            !abortController.signal.aborted;

        // AIDEV-NOTE: Use fetch with credentials: 'include' instead of EventSource
        // This reliably sends auth cookies with the request
        fetch(currentConfig.url, {
            method: "GET",
            credentials: "include",
            headers: {
                Accept: "text/event-stream",
                "Cache-Control": "no-cache",
            },
            signal: abortController.signal,
        })
            .then(async (response) => {
                if (!isActiveConnection()) {
                    return;
                }

                if (!response.ok) {
                    if (response.status === 402) {
                        const responseBody = await response
                            .clone()
                            .json()
                            .catch(() => null);
                        const billingBlocked =
                            parseBillingBlockedResponseBody(responseBody);
                        if (billingBlocked) {
                            throw createBillingLlmBlockedError(billingBlocked);
                        }
                    }
                    if (response.status === 401) {
                        throw new Error("Unauthorized - please log in again");
                    }
                    throw new Error(
                        `HTTP ${response.status}: ${response.statusText}`,
                    );
                }

                if (!response.body) {
                    throw new Error("No response body");
                }

                // AIDEV-NOTE: Track event count for diagnostic logging on stream end/error
                let sseEventCount = 0;
                const httpStatus = response.status;

                // Parse SSE stream
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";

                while (true) {
                    const { done, value } = await reader.read();

                    if (!isActiveConnection()) {
                        return;
                    }

                    if (done) {
                        break;
                    }

                    // Decode chunk and add to buffer
                    buffer += decoder.decode(value, { stream: true });

                    // Process complete SSE messages (separated by double newlines)
                    const messages = buffer.split("\n\n");
                    // Keep incomplete message in buffer
                    buffer = messages.pop() || "";

                    for (const message of messages) {
                        if (!message.trim()) continue;

                        // Parse SSE format: "data: {...}\n"
                        const lines = message.split("\n");
                        for (const line of lines) {
                            if (line.startsWith("data: ")) {
                                const data = line.slice(6); // Remove "data: " prefix

                                try {
                                    const event = JSON.parse(data) as SSEEvent;

                                    // Track if we received complete for graceful close detection
                                    if (event.type === "complete") {
                                        receivedMainUrl = true;
                                    }

                                    sseEventCount++;
                                    configRef.current.onEvent(event);
                                } catch (err) {
                                    console.error(
                                        "[SSE] Parse error:",
                                        err,
                                        "data:",
                                        data,
                                    );
                                }
                            }
                        }
                    }
                }

                // AIDEV-NOTE: Log stream completion with event count for diagnosis
                meditationVerboseDebugLog("[SSE] Stream ended", {
                    httpStatus,
                    sseEventCount,
                    receivedComplete: receivedMainUrl,
                });
            })
            .catch((error) => {
                // Ignore abort errors (expected when we close the connection)
                if (error.name === "AbortError") {
                    return;
                }

                if (!isActiveConnection()) {
                    return;
                }

                // AIDEV-NOTE: Log error name/message and whether any events were received
                // to help diagnose iOS Safari failures where server completes but client errors
                console.error("[SSE] Connection error:", {
                    errorName: error?.name,
                    errorMessage: error?.message,
                    receivedComplete: receivedMainUrl,
                });

                // Only report error if we didn't receive main_url (graceful close)
                if (!receivedMainUrl) {
                    const latestConfig = configRef.current;
                    const reconnectConfig = latestConfig.reconnect;
                    const shouldReconnect =
                        reconnectConfig?.enabled &&
                        (reconnectConfig.shouldReconnect?.() ?? true) &&
                        isReconnectableSSEError(error);

                    if (shouldReconnect) {
                        if (
                            reconnectConfig &&
                            retryCountRef.current < reconnectConfig.maxRetries
                        ) {
                            reconnectConfig.onReconnecting?.();

                            const delay = Math.min(
                                1000 * Math.pow(2, retryCountRef.current),
                                4000,
                            );

                            retryTimeoutRef.current = window.setTimeout(() => {
                                retryTimeoutRef.current = null;
                                if (!isActiveConnection()) {
                                    return;
                                }
                                connectRef.current();
                            }, delay);
                            retryCountRef.current += 1;
                            return;
                        }

                        reconnectConfig?.onReconnectFailed?.();
                        return;
                    }

                    latestConfig.onError(
                        error instanceof Error
                            ? error
                            : new Error("Connection lost. Please try again."),
                    );
                }
            });
    }, []);
    connectRef.current = connect;

    const close = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        if (retryTimeoutRef.current !== null) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        retryCountRef.current = 0;
    }, []);

    const resetRetryCount = useCallback(() => {
        retryCountRef.current = 0;
    }, []);

    // AIDEV-NOTE: No automatic cleanup - parent component (useStreamingMeditation) manages
    // connection lifecycle via explicit close() calls. This avoids React Strict Mode
    // double-invocation issues where cleanup would abort an active connection.

    return { connect, close, resetRetryCount };
}
